import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, XCircle, Facebook, RefreshCw, LogOut, Clock, Settings, History, AlertTriangle } from 'lucide-react'
import { Dialog, ModernButton, Toast, ToastType } from '../../../components/ui'
import { apiService, WhatsAppConfig, WhatsAppOAuthLog } from '../../../services/api'

interface WhatsAppInfoDialogProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export const WhatsAppInfoDialog: React.FC<WhatsAppInfoDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation()

  const [configs, setConfigs] = useState<WhatsAppConfig[]>([])
  const [selectedConfigIndex, setSelectedConfigIndex] = useState(0)
  const [logs, setLogs] = useState<WhatsAppOAuthLog[]>([])
  const [loading, setLoading] = useState(true)
  const [reconnecting, setReconnecting] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)

  useEffect(() => {
    if (isOpen) {
      loadConfig()
    }
  }, [isOpen])

  const loadConfig = async () => {
    try {
      setLoading(true)

      // Buscar configs (plural) e logs em paralelo
      const [configsData, logsData] = await Promise.all([
        apiService.getWhatsAppConfigs().catch(() => []),
        apiService.getWhatsAppOAuthLogs(5).catch(() => ({ logs: [] })),
      ])

      setConfigs(configsData)
      setLogs(logsData.logs)
    } catch (err: any) {
      setToast({ message: t('whatsapp.errors.load'), type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleReconnect = async () => {
    try {
      setReconnecting(true)

      // Busca URL OAuth do backend. O callback do backend termina o fluxo
      // redirecionando esta mesma aba de volta para /marketplace?oauth=..., por
      // isso navegamos a aba atual em vez de abrir um popup.
      const { url: oauthUrl } = await apiService.getMetaOAuthUrl()
      window.location.href = oauthUrl
    } catch (err: any) {
      setToast({
        message: err.message || t('whatsapp.errors.reconnect'),
        type: 'error'
      })
    } finally {
      setReconnecting(false)
    }
  }

  const handleDisconnectClick = () => {
    setShowDisconnectConfirm(true)
  }

  const handleDisconnectConfirm = async () => {
    try {
      setDisconnecting(true)
      setShowDisconnectConfirm(false)
      await apiService.deleteWhatsAppConfig()
      setToast({ message: t('whatsapp.success.disconnected'), type: 'success' })

      setTimeout(async () => {
        await onSuccess()
        onClose()
      }, 1500)
    } catch (err: any) {
      setToast({ message: t('whatsapp.errors.disconnect'), type: 'error' })
    } finally {
      setDisconnecting(false)
    }
  }

  const handleDisconnectCancel = () => {
    setShowDisconnectConfirm(false)
  }

  const formatDate = (date: string | Date | null | undefined) => {
    if (!date) return t('whatsapp.info.never')
    return new Date(date).toLocaleString('pt-BR')
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('whatsapp.info.dialogTitle')}
      size="lg"
    >
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
        </div>
      ) : configs.length === 0 ? (
        <div className="text-center py-8">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <p className="text-foreground font-semibold mb-2">{t('whatsapp.info.noConfig')}</p>
          <p className="text-muted-foreground text-sm">
            {t('whatsapp.info.noConfigHint')}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Seletor de Conta */}
          {configs.length > 1 && (
            <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
              <label className="text-sm font-semibold text-foreground mb-2 block">
                Selecione a conta
              </label>
              <select
                value={selectedConfigIndex}
                onChange={(e) => setSelectedConfigIndex(Number(e.target.value))}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
              >
                {configs.map((config, index) => (
                  <option key={config.id} value={index}>
                    {config.accountName || `Conta ${index + 1}`}
                    {config.isPrimary ? ' (Principal)' : ''}
                    {config.isActive ? ' ✓' : ' ✗'}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Detalhes da Conta Selecionada */}
          {(() => {
            const config = configs[selectedConfigIndex]
            if (!config) return null

            return (
              <div className="space-y-4">
                {/* Header da conta */}
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-foreground">
                      {config.accountName || `Conta ${selectedConfigIndex + 1}`}
                    </h3>
                    {config.isPrimary && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-primary/20 text-primary rounded">
                        Principal
                      </span>
                    )}
                  </div>
                  {config.isActive ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                </div>

                {/* Informações */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Phone Number ID</p>
                    <p className="text-sm text-foreground font-mono">{config.phoneNumberId}</p>
                  </div>

                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Business Account ID</p>
                    <p className="text-sm text-foreground font-mono">{config.businessAccountId}</p>
                  </div>
                </div>

                {/* Timestamps */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <Clock className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="text-xs font-semibold text-blue-900 dark:text-blue-100">{t('whatsapp.info.lastSync')}</p>
                      <p className="text-xs text-blue-700 dark:text-blue-200">{formatDate(config.lastSyncAt)}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <Clock className="h-5 w-5 text-gray-600" />
                    <div>
                      <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">{t('whatsapp.info.createdAt')}</p>
                      <p className="text-xs text-gray-700 dark:text-gray-300">{formatDate(config.createdAt)}</p>
                    </div>
                  </div>
                </div>

                {/* Configurações */}
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Settings className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm font-semibold text-foreground">{t('whatsapp.info.configurations')}</p>
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm text-foreground">
                      <input
                        type="checkbox"
                        checked={config.autoReply}
                        disabled
                        className="rounded"
                      />
                      {t('whatsapp.info.autoReplyEnabled')}
                    </label>

                    <label className="flex items-center gap-2 text-sm text-foreground">
                      <input
                        type="checkbox"
                        checked={config.autoCreateLead}
                        disabled
                        className="rounded"
                      />
                      {t('whatsapp.info.autoCreateLeadEnabled')}
                    </label>
                  </div>
                </div>

                {/* Erro (se houver) */}
                {config.lastError && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-xs font-semibold text-red-900 dark:text-red-100 mb-1">{t('whatsapp.info.lastError')}</p>
                    <p className="text-xs text-red-700 dark:text-red-200">{config.lastError}</p>
                  </div>
                )}
              </div>
            )
          })()}

          {/* Histórico de Conexões */}
          {logs.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <History className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-semibold text-foreground">Histórico de Conexões</p>
              </div>

              <div className="space-y-2 max-h-48 overflow-y-auto">
                {logs.map((log) => (
                  <div
                    key={log.id}
                    className={`p-3 rounded-lg border ${
                      log.status === 'success'
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                        : log.status === 'error'
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {log.status === 'success' ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                        ) : log.status === 'error' ? (
                          <XCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                        ) : (
                          <Clock className="h-4 w-4 text-gray-600 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-foreground capitalize">
                            {log.step.replace(/_/g, ' ')}
                          </p>
                          {log.errorMessage && (
                            <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                              {log.errorMessage}
                            </p>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(log.createdAt).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Webhook Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="font-semibold text-sm text-blue-900 dark:text-blue-100 mb-1">
              {t('whatsapp.info.webhookUrl')}
            </p>
            <p className="text-xs text-blue-800 dark:text-blue-200">
              {t('whatsapp.info.webhookHint')}
              <code className="block bg-blue-100 dark:bg-blue-900/40 px-2 py-1 rounded text-xs mt-1">
                {window.location.origin}/api/public/whatsapp/webhook
              </code>
            </p>
          </div>

          {/* Confirmação de Desconexão */}
          {showDisconnectConfirm && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-sm text-red-900 dark:text-red-100 mb-1">
                    {t('whatsapp.info.confirmDisconnect')}
                  </p>
                  <p className="text-xs text-red-700 dark:text-red-300 mb-3">
                    {t('whatsapp.info.confirmDisconnectWarning')}
                  </p>
                  <div className="flex gap-2">
                    <ModernButton
                      variant="outline"
                      onClick={handleDisconnectCancel}
                      size="sm"
                    >
                      {t('actions.cancel')}
                    </ModernButton>
                    <ModernButton
                      onClick={handleDisconnectConfirm}
                      disabled={disconnecting}
                      size="sm"
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      {disconnecting ? t('whatsapp.info.disconnecting') : t('whatsapp.info.confirmDisconnectButton')}
                    </ModernButton>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Ações */}
          <div className="flex items-center gap-2 pt-4 border-t border-border">
            <ModernButton
              variant="outline"
              onClick={handleReconnect}
              disabled={reconnecting || disconnecting}
            >
              <Facebook className="h-4 w-4 mr-2" />
              {reconnecting ? t('whatsapp.info.reconnecting') : t('whatsapp.info.reconnect')}
            </ModernButton>

            <ModernButton
              variant="outline"
              onClick={handleDisconnectClick}
              disabled={reconnecting || disconnecting}
              className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <LogOut className="h-4 w-4 mr-2" />
              {disconnecting ? t('whatsapp.info.disconnecting') : t('whatsapp.info.disconnect')}
            </ModernButton>

            <ModernButton
              variant="outline"
              onClick={onClose}
              disabled={reconnecting || disconnecting}
              className="ml-auto"
            >
              {t('whatsapp.info.close')}
            </ModernButton>
          </div>
        </div>
      )}
    </Dialog>
  )
}
